import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant } from 'AppConstant'
import { mergePickupBoardByPatient } from '../../utils/mergePickupBoardByPatient'
import { resolveShopMediaUrl } from '../../utils/shopMedia'
import { APIStatus } from '../types'

export interface IShopBoardItem {
  orderItemId: string
  productId: string
  name: string
  imageUrl?: string
  quantity: number
}

export interface IShopOrderCard {
  orderId: string
  patientId: string
  firstname?: string
  lastname?: string
  profilePic?: string | null
  itemCount: number
  items: IShopBoardItem[]
}

export interface IShopInventoryTotal {
  productId: string
  name: string
  imageUrl?: string
  quantity: number
}

export interface IShopBoard {
  count: number
  orders: IShopOrderCard[]
  inventory: IShopInventoryTotal[]
  studioInventory: IShopInventoryTotal[]
}

export interface IVisitShopItem {
  orderItemId: string
  orderId: string
  productId: string
  name: string
  imageUrl?: string
  quantityLabel?: string
  quantity: number
  delivered: boolean
  orderedAt?: string
}

interface ShopState {
  board: IShopBoard
  boardStatus: APIStatus
  visitItems: IVisitShopItem[]
  visitItemsVisitId: string | null
  visitItemsStatus: APIStatus
  markStatus: APIStatus
  error: any
}

const initialState: ShopState = {
  board: { count: 0, orders: [], inventory: [], studioInventory: [] },
  boardStatus: APIStatus.idle,
  visitItems: [],
  visitItemsVisitId: null,
  visitItemsStatus: APIStatus.idle,
  markStatus: APIStatus.idle,
  error: null,
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(AppConstant.DOCTOR_TOKEN)}`,
  'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
})

const ok = (r: any) => r && r.status >= 200 && r.status < 300

function mapInventoryItems(items: IShopInventoryTotal[] = []): IShopInventoryTotal[] {
  return items.map((item) => ({
    ...item,
    imageUrl: resolveShopMediaUrl(item.imageUrl),
  }))
}

function normalizeBoard(board: IShopBoard): IShopBoard {
  const withMedia: IShopBoard = {
    ...board,
    orders: board.orders.map((order) => ({
      ...order,
      profilePic: resolveShopMediaUrl(order.profilePic) ?? null,
      items: order.items.map((item) => ({
        ...item,
        imageUrl: resolveShopMediaUrl(item.imageUrl),
      })),
    })),
    inventory: mapInventoryItems(board.inventory),
    studioInventory: mapInventoryItems(board.studioInventory),
  }

  return mergePickupBoardByPatient(withMedia)
}

function normalizeVisitItems(items: IVisitShopItem[]): IVisitShopItem[] {
  return items.map((item) => {
    const raw = item as IVisitShopItem & { createdAt?: string; orderCreatedAt?: string }
    return {
      ...item,
      orderedAt: item.orderedAt ?? raw.orderCreatedAt ?? raw.createdAt,
      imageUrl: resolveShopMediaUrl(item.imageUrl),
    }
  })
}

// Shop endpoints return raw REST bodies (no { code, data } wrapper).
export const getStudioPickupBoardAsync = createAsyncThunk(
  'shop/getStudioPickupBoard',
  async ({ clinicId }: { clinicId: string }, thunkAPI) => {
    const res = await axios
      .get(`${API_URL}/shop/studio/${clinicId}/orders`, { headers: authHeaders() })
      .then((r) => r)
      .catch((e) => e.response)
    if (ok(res)) return thunkAPI.fulfillWithValue(res.data as IShopBoard)
    return thunkAPI.rejectWithValue('Failed to load shop orders')
  },
)

export const getVisitItemsAsync = createAsyncThunk(
  'shop/getVisitItems',
  async ({ visitId }: { visitId: string }, thunkAPI) => {
    const res = await axios
      .get(`${API_URL}/shop/visit/${visitId}/items`, { headers: authHeaders() })
      .then((r) => r)
      .catch((e) => e.response)
    if (ok(res)) return thunkAPI.fulfillWithValue(res.data.items as IVisitShopItem[])
    return thunkAPI.rejectWithValue('Failed to load order items')
  },
)

export const markItemDeliveredAsync = createAsyncThunk(
  'shop/markItemDelivered',
  async (
    { orderItemId, visitId, delivered }: { orderItemId: string; visitId: string; delivered: boolean },
    thunkAPI,
  ) => {
    const res = await axios
      .put(`${API_URL}/shop/order-items/${orderItemId}/delivered`, { visitId, delivered }, { headers: authHeaders() })
      .then((r) => r)
      .catch((e) => e.response)
    if (ok(res)) return thunkAPI.fulfillWithValue({ orderItemId, delivered })
    return thunkAPI.rejectWithValue('Failed to update delivery')
  },
)

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    applyVisitItemsDelivered: (
      state,
      action: PayloadAction<{
        orderItemIds: string[]
        delivered: boolean
        productId: string
        inventoryQuantity: number
      }>,
    ) => {
      const { orderItemIds, delivered, productId, inventoryQuantity } = action.payload

      for (const orderItemId of orderItemIds) {
        const line = state.visitItems.find((it) => it.orderItemId === orderItemId)
        if (line) line.delivered = delivered
      }

      if (inventoryQuantity > 0) {
        const inv = state.board.studioInventory.find((row) => row.productId === productId)
        if (inv) {
          const delta = delivered ? -inventoryQuantity : inventoryQuantity
          inv.quantity = Math.max(0, inv.quantity + delta)
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStudioPickupBoardAsync.pending, (state) => {
        state.boardStatus = APIStatus.pending
      })
      .addCase(getStudioPickupBoardAsync.fulfilled, (state, action) => {
        state.boardStatus = APIStatus.fulfilled
        state.board = normalizeBoard(action.payload)
      })
      .addCase(getStudioPickupBoardAsync.rejected, (state, action) => {
        state.boardStatus = APIStatus.rejected
        state.error = action.payload
      })
      .addCase(getVisitItemsAsync.pending, (state, action) => {
        state.visitItemsStatus = APIStatus.pending
        state.visitItems = []
        state.visitItemsVisitId = action.meta.arg.visitId
      })
      .addCase(getVisitItemsAsync.fulfilled, (state, action) => {
        if (action.meta.arg.visitId !== state.visitItemsVisitId) return
        state.visitItemsStatus = APIStatus.fulfilled
        state.visitItems = normalizeVisitItems(action.payload)
      })
      .addCase(getVisitItemsAsync.rejected, (state, action) => {
        if (action.meta.arg.visitId !== state.visitItemsVisitId) return
        state.visitItemsStatus = APIStatus.rejected
        state.visitItems = []
      })
      .addCase(markItemDeliveredAsync.pending, (state) => {
        state.markStatus = APIStatus.pending
      })
      .addCase(markItemDeliveredAsync.fulfilled, (state) => {
        state.markStatus = APIStatus.fulfilled
      })
      .addCase(markItemDeliveredAsync.rejected, (state) => {
        state.markStatus = APIStatus.rejected
      })
  },
})

export const { applyVisitItemsDelivered } = shopSlice.actions

export default shopSlice.reducer
